import { useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";
import { MapPin, ArrowClockwise, DownloadSimple, Warning } from "@phosphor-icons/react";
import Layout from "../components/Layout";
import api from "../lib/api";

const STATUS_STYLE = {
  confirmed: { bg: "#CCFBF1", fg: "#0F766E", label: "Confirmed" },
  matched: { bg: "#DCFCE7", fg: "#166534", label: "Matched" },
  weak: { bg: "#FEF9C3", fg: "#854D0E", label: "Weak" },
  unmatched: { bg: "#FEE2E2", fg: "#991B1B", label: "No SAP match" },
  drift: { bg: "#FFE4E6", fg: "#9F1239", label: "Drift" },
  sap_only: { bg: "#E0E7FF", fg: "#3730A3", label: "SAP only" },
};
const SCOPE_LABEL = { dispatch: "Dispatch-plan", invoiced: "Invoiced (3m)", both: "Both" };

const th = { padding: "9px 10px", fontSize: 11, fontWeight: 800, color: "#fff", textAlign: "left", whiteSpace: "nowrap" };
const td = { padding: "8px 10px", fontSize: 12, borderBottom: "1px solid #F1F5F9", verticalAlign: "top" };

const Tile = ({ label, value, color }) => (
  <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, padding: "12px 16px", minWidth: 104 }}>
    <div style={{ fontSize: 10, fontWeight: 800, color: "#94A3B8", textTransform: "uppercase", letterSpacing: 0.4 }}>{label}</div>
    <div style={{ fontSize: 22, fontWeight: 800, color: color || "#0F172A" }}>{value ?? "—"}</div>
  </div>
);

export default function AddressMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scope, setScope] = useState("both");
  const [filter, setFilter] = useState("all");
  const [downloading, setDownloading] = useState(false);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/sap/address-map");
      setData(data);
      if (data.scope) setScope(data.scope);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load address map");
    }
  }, []);

  const startPolling = useCallback(() => {
    if (pollRef.current) return;
    pollRef.current = setInterval(async () => {
      const d = await load();
      if (d && !d.building) {
        clearInterval(pollRef.current);
        pollRef.current = null;
        toast.success("Address map rebuilt");
      }
    }, 5000);
  }, [load]);

  useEffect(() => {
    (async () => { setLoading(true); const d = await load(); setLoading(false); if (d?.building) startPolling(); })();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [load, startPolling]);

  const rebuild = async () => {
    try {
      await api.post(`/sap/address-map/rebuild?scope=${scope}`);
      toast.info(`Rebuilding (${SCOPE_LABEL[scope]})… this runs in the background`);
      await load();
      startPolling();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rebuild failed");
    }
  };

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/sap/address-map/export.xlsx", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
      const a = document.createElement("a"); a.href = url; a.download = "address_map.xlsx";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    } finally { setDownloading(false); }
  };

  const s = data?.summary || {};
  const rows = (data?.rows || []).filter((r) => filter === "all" || r.status === filter);
  const building = data?.building;

  return (
    <Layout>
      <div data-testid="address-map-page" style={{ padding: "22px 26px", maxWidth: 1420, margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
          <MapPin size={26} weight="duotone" color="#0F766E" />
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "#0F172A", margin: 0 }}>Overall Address Map</h1>
          <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
            <button data-testid="address-map-download" onClick={download} disabled={downloading}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 9, border: "1px solid #CBD5E1", background: "#fff", fontSize: 12.5, fontWeight: 700, color: "#0F172A", cursor: downloading ? "default" : "pointer" }}>
              <DownloadSimple size={15} weight="bold" /> {downloading ? "Preparing…" : "Excel"}
            </button>
            <button data-testid="address-map-rebuild" onClick={rebuild} disabled={building}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 13px", borderRadius: 9, border: "none", background: building ? "#94A3B8" : "#0F766E", color: "#fff", fontSize: 12.5, fontWeight: 700, cursor: building ? "default" : "pointer" }}>
              <ArrowClockwise size={15} weight="bold" className={building ? "spin" : ""} /> {building ? "Rebuilding…" : "Rebuild"}
            </button>
          </div>
        </div>
        <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 16px" }}>
          Match between MS&nbsp;SQL ERP ship-to addresses (<code>dbo.paddress</code>) and SAP ByDesign addresses.
          Scope: <b>{SCOPE_LABEL[data?.scope] || "—"}</b>. {data?.rebuilt_at && <>Last built {String(data.rebuilt_at).slice(0, 16).replace("T", " ")} UTC.</>}
        </p>

        {s.drift > 0 && (
          <div data-testid="address-map-drift-alert" style={{ display: "flex", alignItems: "center", gap: 8, background: "#FFE4E6", border: "1px solid #FDA4AF", color: "#9F1239", borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, fontWeight: 600 }}>
            <Warning size={18} weight="fill" /> {s.drift} confirmed link{s.drift > 1 ? "s" : ""} broke — the locked SAP address no longer exists in SAP. Review the <b>Drift</b> rows and re-confirm.
          </div>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <Tile label="Customers" value={s.pcodes} />
          <Tile label="ERP addr" value={s.erp_addr} />
          <Tile label="SAP addr" value={s.sap_addr} />
          <Tile label="Matched" value={s.matched} color="#166534" />
          <Tile label="Weak" value={s.weak} color="#854D0E" />
          <Tile label="No SAP match" value={s.unmatched_erp} color="#991B1B" />
          <Tile label="Drift" value={s.drift} color="#9F1239" />
          <Tile label="SAP only" value={s.sap_only} color="#3730A3" />
        </div>

        <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
          {["all", "matched", "weak", "unmatched", "drift", "sap_only"].map((f) => (
            <button key={f} data-testid={`address-map-filter-${f}`} onClick={() => setFilter(f)}
              style={{ padding: "5px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700, cursor: "pointer", border: "1px solid " + (filter === f ? "#0F766E" : "#CBD5E1"), background: filter === f ? "#0F766E" : "#fff", color: filter === f ? "#fff" : "#475569" }}>
              {f === "all" ? "All" : (STATUS_STYLE[f]?.label || f)}
            </button>
          ))}
        </div>

        <div style={{ border: "1px solid #E2E8F0", borderRadius: 12, overflow: "auto", background: "#fff" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1180 }}>
            <thead>
              <tr style={{ background: "#0F172A" }}>
                <th style={th}>Customer</th>
                <th style={th}>padd_code</th>
                <th style={th}>ERP Address</th>
                <th style={th}>Match</th>
                <th style={{ ...th, textAlign: "right" }}>Score</th>
                <th style={th}>SAP Address</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={6} style={{ ...td, textAlign: "center", color: "#94A3B8", padding: "28px 0" }}>
                  {loading ? "Loading…" : building ? "Rebuilding — hang tight…" : "No rows. Click Rebuild."}
                </td></tr>
              )}
              {rows.map((r, i) => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.unmatched;
                return (
                  <tr key={i} data-testid={`address-map-row-${i}`} style={{ background: r.drift ? "#FFF1F2" : undefined }}>
                    <td style={{ ...td, fontWeight: 700 }}>{r.pcode}<div style={{ color: "#94A3B8", fontWeight: 400 }}>{r.sap_account} · {SCOPE_LABEL[r.src] || r.src}</div></td>
                    <td style={td}>{r.padd_code || "—"}</td>
                    <td style={{ ...td, maxWidth: 300 }}>
                      <div style={{ fontWeight: 600 }}>{r.erp_consignee || "—"}</div>
                      <div style={{ color: "#64748B" }}>{r.erp_address}</div>
                      <div style={{ color: "#94A3B8" }}>{[r.erp_city, r.erp_state, r.erp_postal].filter(Boolean).join(" · ")}</div>
                    </td>
                    <td style={td}>
                      <span style={{ background: st.bg, color: st.fg, padding: "2px 9px", borderRadius: 999, fontSize: 11, fontWeight: 800, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 4 }}>
                        {st.label}
                      </span>
                    </td>
                    <td style={{ ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.status === "sap_only" ? "—" : (r.score?.toFixed(2))}</td>
                    <td style={{ ...td, maxWidth: 320 }}>
                      <div style={{ color: "#334155" }}>{r.sap_address || "—"}</div>
                      <div style={{ color: "#94A3B8" }}>{[r.sap_usage, r.sap_city, r.sap_postal].filter(Boolean).join(" · ")}</div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
